import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Polyline, CircleMarker, useMap, Popup, Tooltip } from "react-leaflet";
import type { LatLngExpression } from "leaflet";
import { latLngBounds } from "leaflet";
import { useResizeObserver } from "@/hooks/use-resize-observer";
import type { Container } from "@/types/container";
import { generateRealisticRoute, getSeaRouteWaypoints, identifySeaSegments } from "@/utils/route-generator";
import { Map01 } from "@untitledui/icons";

export type Waypoint = {
    id?: string;
    lat: number;
    lng: number;
    label?: string;
    type?: "start" | "stop" | "end";
    info?: {
        portName?: string;
        country?: string;
        eta?: string; // ISO string
        ata?: string; // ISO string
        status?: "planned" | "in_transit" | "arrived" | "delayed";
        carrier?: string;
        vessel?: string;
        voyage?: string;
        notes?: string;
    };
};

interface RouteMapProps {
    /** Waypoints della rotta (opzionale se container è fornito) */
    waypoints?: Waypoint[];
    /** Container opzionale: se fornito, usa container.waypoints o genera automaticamente */
    container?: Container;
    /** Array di coppie [startIndex, endIndex] (inclusivi) per le tratte mare (opzionale, generato automaticamente se non fornito) */
    seaSegments?: Array<[number, number]>;
    /** Avanzamento 0..1 della rotta */
    progress?: number;
    className?: string;
    onStopClick?: (waypoint: Waypoint) => void;
}

function clamp(value: number, min: number, max: number) {
    return Math.max(min, Math.min(max, value));
}

function haversineDistanceKm(a: Waypoint, b: Waypoint): number {
    const R = 6371; // km
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLon = ((b.lng - a.lng) * Math.PI) / 180;
    const lat1 = (a.lat * Math.PI) / 180;
    const lat2 = (b.lat * Math.PI) / 180;
    const sinDLat = Math.sin(dLat / 2);
    const sinDLon = Math.sin(dLon / 2);
    const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
    const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
    return R * c;
}

function useFitBounds(points: Waypoint[]) {
    const map = useMap();
    useEffect(() => {
        if (!points.length) return;
        const boundsTuples: [number, number][] = points.map((p) => [p.lat, p.lng]);
        // padding per non tagliare marker/linee
        map.fitBounds(latLngBounds(boundsTuples), { padding: [20, 20] });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(points)]);
}

function CompletedPolyline({
    waypoints,
    progress,
    color = "var(--color-utility-brand-600)",
    weight = 4,
}: { waypoints: Waypoint[]; progress: number; color?: string; weight?: number }) {
    const segments = useMemo(() => {
        const segs: { from: Waypoint; to: Waypoint; len: number }[] = [];
        for (let i = 0; i < waypoints.length - 1; i++) {
            const from = waypoints[i];
            const to = waypoints[i + 1];
            segs.push({ from, to, len: haversineDistanceKm(from, to) });
        }
        return segs;
    }, [JSON.stringify(waypoints)]);

    const total = segments.reduce((s, it) => s + it.len, 0);
    const target = clamp(progress, 0, 1) * total;

    const polyPoints: LatLngExpression[] = [];
    let acc = 0;
    for (const seg of segments) {
        if (acc >= target) break;
        const remaining = target - acc;
        if (remaining >= seg.len) {
            polyPoints.push([seg.from.lat, seg.from.lng]);
            polyPoints.push([seg.to.lat, seg.to.lng]);
            acc += seg.len;
        } else {
            // interpolazione lineare sul segmento
            const t = remaining / seg.len;
            const lat = seg.from.lat + (seg.to.lat - seg.from.lat) * t;
            const lng = seg.from.lng + (seg.to.lng - seg.from.lng) * t;
            polyPoints.push([seg.from.lat, seg.from.lng]);
            polyPoints.push([lat, lng]);
            acc = target;
            break;
        }
    }

    // deduplica eventuali punti consecutivi uguali
    const dedup: LatLngExpression[] = [];
    for (const p of polyPoints) {
        const last = dedup[dedup.length - 1] as [number, number] | undefined;
        const cur = p as [number, number];
        if (!last || last[0] !== cur[0] || last[1] !== cur[1]) dedup.push(p);
    }

    if (dedup.length < 2) return null;
    return <Polyline positions={dedup} pathOptions={{ color, weight }} />;
}

export function RouteMap({ waypoints: waypointsProp, container, seaSegments: seaSegmentsProp, progress = 0, className, onStopClick }: RouteMapProps) {
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    // Determina i waypoints da usare: container.waypoints (se >2) > waypoints prop > generazione/rotta mare
    const [waypoints, setWaypoints] = useState<Waypoint[]>([]);

    useEffect(() => {
        // Container con più di 2 waypoints: usa quelli (rotta già ricca)
        if (container?.waypoints && container.waypoints.length > 2) {
            setWaypoints(container.waypoints);
            return;
        }
        if (waypointsProp && waypointsProp.length > 0) {
            setWaypoints(waypointsProp);
            return;
        }
        if (container?.route?.from && container?.route?.to) {
            // Waypoints mancanti o solo 2 (origine/destinazione): stima veloce poi rotta mare via API
            const quickRoute = generateRealisticRoute(container.route.from, container.route.to, container.createdAt);
            setWaypoints(quickRoute);
            let cancelled = false;
            getSeaRouteWaypoints(container.route.from, container.route.to, container.createdAt)
                .then((seaRoute) => {
                    if (!cancelled) setWaypoints(seaRoute);
                })
                .catch(() => { /* quick route stays as fallback */ });
            return () => { cancelled = true; };
        }
        if (container) {
            setWaypoints([]);
            return;
        }
        setWaypoints([]);
    }, [container?.route?.from, container?.route?.to, container?.createdAt, container?.waypoints, waypointsProp]);

    // Determina i sea segments: usa quelli forniti o genera automaticamente
    const seaSegments = useMemo(() => {
        if (seaSegmentsProp && seaSegmentsProp.length > 0) {
            return seaSegmentsProp;
        }
        // Genera automaticamente i sea segments basati sulla distanza
        return identifySeaSegments(waypoints);
    }, [seaSegmentsProp, waypoints]);

    // Determina il progress: usa container.progress se disponibile
    const actualProgress = useMemo(() => {
        if (container?.progress !== undefined) {
            return Math.max(0, Math.min(1, container.progress / 100));
        }
        return progress;
    }, [container, progress]);

    const positions: LatLngExpression[] = useMemo(
        () => waypoints.map((w) => [w.lat, w.lng]),
        [JSON.stringify(waypoints)]
    );

    // Check if we have valid waypoints (not at 0,0 origin which is fallback)
    const hasValidRoute = useMemo(() => {
        if (waypoints.length < 2) return false;
        // Check if any waypoint has coordinates other than (0,0)
        return waypoints.some(w => w.lat !== 0 || w.lng !== 0);
    }, [waypoints]);

    const Fit = () => {
        useFitBounds(waypoints);
        return null;
    };

    const ResizeHandler = () => {
        const map = useMap();
        useResizeObserver({
            ref: wrapperRef,
            onResize: () => {
                // Recalibra la mappa dopo cambi dimensione (es. espansione)
                map.invalidateSize();
                if (waypoints.length) {
                    const boundsTuples: [number, number][] = waypoints.map((p) => [p.lat, p.lng]);
                    map.fitBounds(latLngBounds(boundsTuples), { padding: [20, 20] });
                }
            },
        });
        return null;
    };

    // Show placeholder when no valid route data
    if (!hasValidRoute) {
        return (
            <div className={className} ref={wrapperRef}>
                <div className="h-full w-full flex flex-col items-center justify-center bg-secondary/30 rounded-lg">
                    <div className="rounded-full bg-secondary/50 p-4 mb-3">
                        <Map01 className="h-8 w-8 text-tertiary" />
                    </div>
                    <p className="text-sm font-medium text-primary mb-1">Route Unavailable</p>
                    <p className="text-xs text-tertiary text-center max-w-xs px-4">
                        {container?.route?.from && container?.route?.to
                            ? `Route from ${container.route.from} to ${container.route.to}`
                            : "Position data not yet available for this shipment"
                        }
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={className} ref={wrapperRef}>
            <MapContainer style={{ height: "100%", width: "100%" }} zoom={3} center={positions[0] as [number, number]} scrollWheelZoom={false}>
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {/* rotta completa (grigio tema) */}
                <Polyline positions={positions} pathOptions={{ color: "var(--color-utility-gray-300)", weight: 3 }} />

                {/* segmenti mare (blu tema tratteggiato) */}
                {seaSegments.map(([start, end], idx) => {
                    const seg = waypoints.slice(start, end + 1).map((w) => [w.lat, w.lng]) as LatLngExpression[];
                    if (seg.length < 2) return null;
                    return (
                        <Polyline
                            key={`sea-${idx}`}
                            positions={seg}
                            pathOptions={{ color: "var(--color-blue-600)", weight: 3, dashArray: "6 6", opacity: 0.9 }}
                        />
                    );
                })}

                {/* tratto completato sopra la rotta (brand) */}
                <CompletedPolyline waypoints={waypoints} progress={actualProgress} />

                {/* marker */}
                {waypoints.map((w, i) => {
                    const color = w.type === "start"
                        ? "var(--color-success-600)"
                        : w.type === "end"
                            ? "var(--color-error-600)"
                            : "var(--color-brand-500)";
                    const radius = w.type === "start" || w.type === "end" ? 6 : 4;
                    return (
                        <CircleMarker
                            key={w.id ?? i}
                            center={[w.lat, w.lng]}
                            radius={radius}
                            pathOptions={{ color, weight: 2, fillOpacity: 0.9 }}
                            eventHandlers={{ click: () => onStopClick?.(w) }}
                        >
                            {w.label && <Tooltip direction="top" offset={[0, -radius]}>{w.label}</Tooltip>}
                            <Popup>
                                <div className="space-y-1" style={{ color: "rgb(24, 29, 39)" }}>
                                    <div className="text-sm font-semibold" style={{ color: "rgb(24, 29, 39)" }}>
                                        {w.info?.portName ?? w.label ?? "Stop"}
                                    </div>
                                    {w.info?.country && (
                                        <div className="text-xs" style={{ color: "rgb(83, 88, 98)" }}>{w.info.country}</div>
                                    )}
                                    {w.info?.status && (
                                        <div className="text-xs">
                                            <span
                                                className="inline-flex items-center rounded px-1.5 py-0.5"
                                                style={{
                                                    backgroundColor:
                                                        w.info.status === "arrived" ? "var(--color-success-50)" :
                                                            w.info.status === "in_transit" ? "var(--color-brand-50)" :
                                                                w.info.status === "delayed" ? "var(--color-error-50)" : "var(--color-gray-100)",
                                                    color:
                                                        w.info.status === "arrived" ? "var(--color-success-600)" :
                                                            w.info.status === "in_transit" ? "var(--color-brand-600)" :
                                                                w.info.status === "delayed" ? "var(--color-error-600)" : "var(--color-gray-600)",
                                                    border: "1px solid var(--color-border-tertiary)",
                                                }}
                                            >
                                                {w.info.status}
                                            </span>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs" style={{ color: "rgb(65, 70, 81)" }}>
                                        {w.info?.eta && (<><div>ETA</div><div>{new Date(w.info.eta).toLocaleString()}</div></>)}
                                        {w.info?.ata && (<><div>ATA</div><div>{new Date(w.info.ata).toLocaleString()}</div></>)}
                                        {w.info?.carrier && (<><div>Carrier</div><div>{w.info.carrier}</div></>)}
                                        {w.info?.vessel && (<><div>Vessel</div><div>{w.info.vessel}</div></>)}
                                        {w.info?.voyage && (<><div>Voyage</div><div>{w.info.voyage}</div></>)}
                                    </div>
                                    {w.info?.notes && (
                                        <div className="text-xs" style={{ color: "rgb(83, 88, 98)" }}>
                                            {w.info.notes}
                                        </div>
                                    )}
                                </div>
                            </Popup>
                        </CircleMarker>
                    );
                })}

                <Fit />
                <ResizeHandler />
            </MapContainer>
        </div>
    );
}

export default RouteMap;


